import useSWR from "swr";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  MapPin,
  Cpu,
  ShieldCheck,
  Buildings,
  LinkSimple,
  Warning,
} from "@phosphor-icons/react";
import { DETAIL } from "@/constants/testIds";

const fetcher = async (id) => (await api.get(`/hosts/${id}`)).data;

const tierBadge = (tier) =>
  tier === "Gold"
    ? "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]"
    : "bg-[#F3F4F6] text-[#374151] border-[#9CA3AF]";

const envBadge = (env) => {
  switch (env) {
    case "Prod": return "bg-[#FEE2E2] text-[#991B1B] border-[#F87171]";
    case "Dev": return "bg-[#E0F2FE] text-[#075985] border-[#38BDF8]";
    case "Sandbox": return "bg-[#FEF9C3] text-[#854D0E] border-[#FACC15]";
    case "Test": return "bg-[#F3E8FF] text-[#6B21A8] border-[#C084FC]";
    default: return "bg-[#F3F4F6] text-[#374151] border-[#9CA3AF]";
  }
};

export function HostDetail({ hostId, onBackToInventory, onOpenHost }) {
  const { data: host, error, isLoading } = useSWR(
    ["host", hostId],
    () => fetcher(hostId)
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !host) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-8 text-center">
        <Warning size={32} weight="fill" className="text-[#E11D48] mx-auto" />
        <div className="font-heading font-bold text-lg mt-2">Host not found</div>
        <div className="text-sm text-[#6B7280] mt-1">
          It may have been removed. Return to the inventory to refresh.
        </div>
        <Button
          onClick={onBackToInventory}
          className="mt-4 rounded-sm bg-[#0052CC] hover:bg-[#0043A6] text-white font-heading font-bold uppercase tracking-wide text-xs"
        >
          <ArrowLeft size={14} weight="bold" className="mr-1.5" />
          Back to inventory
        </Button>
      </div>
    );
  }

  const isServer = host.host_type === "Server";
  const showHA = host.host_type === "Router" || host.host_type === "Firewall";

  return (
    <div data-testid={DETAIL.root(hostId)} className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono-tech uppercase tracking-widest text-[#6B7280]">
            HOST · {host.host_type}
          </div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-1 font-mono-tech">
            {host.hostname}
          </h1>
          <div className="mt-1 flex items-center gap-3 text-[13px] text-[#4B5563]">
            <span className="font-mono-tech text-[#111827]">{host.ip}</span>
            <span className="text-[#D1D5DB]">·</span>
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} weight="fill" className="text-[#0052CC]" />
              {host.location_name}
            </span>
          </div>
        </div>
        <Button
          data-testid={DETAIL.backBtn(hostId)}
          onClick={onBackToInventory}
          variant="outline"
          className="rounded-sm border-[#111827] text-[#111827] font-heading font-bold uppercase tracking-wide text-xs hover:bg-[#111827] hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" className="mr-1.5" />
          Back to inventory
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Host Information */}
        <Section
          testId={DETAIL.hostSection}
          icon={<Cpu size={16} weight="bold" />}
          label="Host Information"
          number="01"
        >
          <FieldGrid>
            <Field label="Hostname" value={host.hostname} mono />
            <Field label="IP Address" value={host.ip} mono />
            <Field label="MAC Address" value={host.mac_address} mono />
            <Field label="Serial Number" value={host.serial_number} mono />
            <Field label="Vendor" value={host.vendor} />
            <Field label="Model" value={host.model} />
            <Field label="System Type" value={host.host_type} />
            <Field label="OS" value={host.os} />
            <Field label="OS Version" value={host.os_version} mono />
          </FieldGrid>
        </Section>

        {/* HA Information — routers/firewalls only */}
        <Section
          testId={DETAIL.haSection}
          icon={<ShieldCheck size={16} weight="bold" />}
          label="HA Information"
          number="02"
          disabled={!showHA}
          disabledMessage="HA data only applies to routers and firewalls."
        >
          {showHA && (
            <>
              <FieldGrid>
                <Field
                  label="HA Role"
                  value={
                    host.ha_role ? (
                      <Badge
                        className={`rounded-sm border font-heading font-bold uppercase text-[10px] tracking-wider px-1.5 py-0 ${
                          host.ha_role === "Primary"
                            ? "bg-[#DCFCE7] text-[#166534] border-[#22C55E]"
                            : "bg-[#E0F2FE] text-[#075985] border-[#38BDF8]"
                        }`}
                      >
                        {host.ha_role}
                      </Badge>
                    ) : (
                      "—"
                    )
                  }
                />
                <Field
                  label="Peer Count"
                  value={String((host.ha_peers || []).length)}
                  mono
                />
              </FieldGrid>
              <div className="mt-4">
                <div className="text-[9px] font-heading font-bold uppercase tracking-widest text-[#6B7280]">
                  HA Peers
                </div>
                {(host.ha_peers || []).length === 0 ? (
                  <div className="mt-1.5 text-sm text-[#9CA3AF]">
                    Standalone — no peers configured.
                  </div>
                ) : (
                  <ul className="mt-1.5 space-y-1">
                    {host.ha_peers.map((peer, i) => (
                      <li
                        key={peer}
                        data-testid={DETAIL.peer(i)}
                        className="flex items-center justify-between border border-[#E5E7EB] rounded-sm px-2.5 py-1.5"
                      >
                        <span className="font-mono-tech text-[13px] text-[#111827]">
                          {peer}
                        </span>
                        <button
                          onClick={() =>
                            onOpenHost && onOpenHost({ id: peer, hostname: peer, host_type: host.host_type })
                          }
                          className="text-[10px] font-heading font-bold uppercase tracking-wider text-[#0052CC] hover:text-[#0043A6] inline-flex items-center gap-1"
                          title="Copy hostname (peer id unknown from list)"
                          disabled
                        >
                          <LinkSimple size={12} weight="bold" />
                          Peer
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </Section>

        {/* Location Information */}
        <Section
          testId={DETAIL.locationSection}
          icon={<Buildings size={16} weight="bold" />}
          label="Location Information"
          number="03"
        >
          <FieldGrid>
            <Field label="Location Name" value={host.location_name} wide />
            <Field label="Street Address" value={host.location_address} wide />
            <Field label="Building Location" value={host.building_location} wide />
          </FieldGrid>
        </Section>

        {/* System Information — servers only */}
        <Section
          testId={DETAIL.systemSection}
          icon={<LinkSimple size={16} weight="bold" />}
          label="System Information"
          number="04"
          disabled={!isServer}
          disabledMessage="System, tier, and environment are only tracked for Servers."
        >
          {isServer && (
            <FieldGrid>
              <Field label="System" value={host.system_name || "—"} />
              <Field
                label="Tier"
                value={
                  host.system_tier ? (
                    <Badge
                      className={`rounded-sm border font-heading font-bold uppercase text-[10px] tracking-wider px-1.5 py-0 ${tierBadge(host.system_tier)}`}
                    >
                      {host.system_tier}
                    </Badge>
                  ) : (
                    "—"
                  )
                }
              />
              <Field
                label="Environment"
                value={
                  host.environment ? (
                    <Badge
                      className={`rounded-sm border font-heading font-bold uppercase text-[10px] tracking-wider px-1.5 py-0 ${envBadge(host.environment)}`}
                    >
                      {host.environment}
                    </Badge>
                  ) : (
                    "—"
                  )
                }
              />
            </FieldGrid>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ testId, icon, label, number, disabled, disabledMessage, children }) {
  return (
    <section
      data-testid={testId}
      className={`bg-white border border-[#E5E7EB] rounded-sm ${
        disabled ? "opacity-60" : ""
      }`}
    >
      <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="h-7 w-7 grid place-items-center bg-[#0052CC] text-white">
            {icon}
          </span>
          <div>
            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-[#6B7280]">
              {number}
            </div>
            <div className="font-heading font-black text-[15px] tracking-tight leading-none mt-0.5">
              {label}
            </div>
          </div>
        </div>
      </div>
      <div className="p-5">
        {disabled ? (
          <div className="text-sm text-[#6B7280] italic">{disabledMessage}</div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

function FieldGrid({ children }) {
  return <div className="grid grid-cols-2 gap-x-6 gap-y-4">{children}</div>;
}

function Field({ label, value, mono, wide }) {
  const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[9px] font-heading font-bold uppercase tracking-widest text-[#6B7280]">
        {label}
      </div>
      <div
        data-testid={DETAIL.field(key)}
        className={`mt-1 text-[#111827] text-[14px] ${mono ? "font-mono-tech" : "font-medium"}`}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}
